import { Background, Controls, ReactFlow, type ReactFlowInstance } from "@xyflow/react";
import { type DragEvent, useCallback, useEffect, useMemo, useState } from "react";
import "@xyflow/react/dist/style.css";
import { useActivityTypes } from "../../hooks/useActivityTypes.js";
import { useCanvasState } from "../../hooks/useCanvasState.js";
import { useExecutionMonitor } from "../../hooks/useExecutionMonitor.js";
import { useWorkflow } from "../../hooks/useWorkflow.js";
import { api } from "../../lib/api-client.js";
import { type StepNodeData, type WorkflowState, toYaml } from "../../lib/yaml-serializer.js";
import { StepConfigPanel } from "../config/StepConfigPanel.js";
import { MonitorToolbar } from "../monitor/MonitorToolbar.js";
import { StepInspectPanel } from "../monitor/StepInspectPanel.js";
import { NodePalette } from "../palette/NodePalette.js";
import { YamlPreview } from "../yaml/YamlPreview.js";
import { CanvasToolbar } from "./CanvasToolbar.js";
import { DataEdge } from "./DataEdge.js";
import { StepNode } from "./StepNode.js";

const nodeTypes = { stepNode: StepNode };
const edgeTypes = { default: DataEdge };

interface WorkflowCanvasProps {
	initialState?: WorkflowState;
}

export function WorkflowCanvas({ initialState }: WorkflowCanvasProps) {
	const canvas = useCanvasState();
	const activityTypes = useActivityTypes();
	const { deploy } = useWorkflow();
	const monitor = useExecutionMonitor();
	const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
	const [yamlOpen, setYamlOpen] = useState(false);
	const [deploying, setDeploying] = useState(false);
	const [workflowName, setWorkflowName] = useState(initialState?.name ?? "new-workflow");
	const [workflowId, setWorkflowId] = useState<string | null>(initialState?.id ?? null);
	const [trigger, setTrigger] = useState<WorkflowState["trigger"]>(
		initialState?.trigger ?? { type: "api" },
	);

	// Initialize from loaded state
	const [initialized, setInitialized] = useState(false);
	useEffect(() => {
		if (initialState && !initialized && initialState.nodes.length > 0) {
			canvas.setNodes(initialState.nodes);
			canvas.setEdges(initialState.edges);
			setInitialized(true);
		}
	}, [initialState, initialized, canvas]);

	const yaml = useMemo(
		() =>
			toYaml({
				id: workflowId,
				name: workflowName,
				version: 1,
				trigger,
				inputs: initialState?.inputs ?? {},
				nodes: canvas.nodes,
				edges: canvas.edges,
				selectedNodeId: null,
				isDirty: false,
			}),
		[canvas.nodes, canvas.edges, workflowName, workflowId, trigger, initialState],
	);

	// Inject execution state into nodes when monitoring
	const displayNodes = useMemo(() => {
		if (!monitor.isMonitoring) return canvas.nodes;
		return canvas.nodes.map((node) => {
			const nodeData = node.data as unknown as StepNodeData;
			const executionState = monitor.steps.get(nodeData.stepId);
			if (!executionState) return node;
			return { ...node, data: { ...node.data, executionState } };
		});
	}, [canvas.nodes, monitor.isMonitoring, monitor.steps]);

	const selectedNode = canvas.nodes.find((n) => n.id === canvas.selectedNodeId);

	// Determine execution state for selected node (monitor mode)
	const selectedNodeData = selectedNode ? (selectedNode.data as unknown as StepNodeData) : null;
	const selectedStepExecState = selectedNodeData
		? monitor.steps.get(selectedNodeData.stepId)
		: undefined;

	const onDrop = useCallback(
		(e: DragEvent) => {
			e.preventDefault();
			if (monitor.isMonitoring) return;
			const activityType = e.dataTransfer.getData("application/forge-activity");
			if (!activityType || !reactFlowInstance) return;

			const position = reactFlowInstance.screenToFlowPosition({
				x: e.clientX,
				y: e.clientY,
			});
			canvas.addNode(activityType, position);
		},
		[reactFlowInstance, canvas, monitor.isMonitoring],
	);

	const onDragOver = useCallback(
		(e: DragEvent) => {
			e.preventDefault();
			if (monitor.isMonitoring) return;
			e.dataTransfer.dropEffect = "move";
		},
		[monitor.isMonitoring],
	);

	const handleDeploy = async () => {
		setDeploying(true);
		const state: WorkflowState = {
			id: workflowId,
			name: workflowName,
			version: 1,
			trigger,
			inputs: initialState?.inputs ?? {},
			nodes: canvas.nodes,
			edges: canvas.edges,
			selectedNodeId: null,
			isDirty: false,
		};
		const result = await deploy(state);
		if (result) {
			setWorkflowId(result.id);
		}
		setDeploying(false);
	};

	const handleRun = async () => {
		if (!workflowId) return;
		try {
			const result = await api.post<{ execution_id: string }>(`/workflows/${workflowId}/trigger`, {
				inputs: initialState?.inputs ?? {},
			});
			monitor.start(result.execution_id);
		} catch {
			// Could not trigger
		}
	};

	return (
		<div className="flex h-screen bg-canvas" id="main-content">
			{/* Left: Palette — hidden during monitor mode */}
			{!monitor.isMonitoring && (
				<div className="w-64 min-w-64 border-r border-border bg-surface">
					<NodePalette
						types={activityTypes.types}
						loading={activityTypes.loading}
						error={activityTypes.error}
						onRetry={activityTypes.retry}
					/>
				</div>
			)}

			{/* Center: Canvas */}
			<div className="relative flex-1">
				{monitor.isMonitoring ? (
					<MonitorToolbar
						workflowName={workflowName}
						executionStatus={monitor.executionStatus}
						elapsed={monitor.elapsed}
						onCancel={monitor.cancel}
						onStop={monitor.stop}
					/>
				) : (
					<CanvasToolbar
						workflowName={workflowName}
						version={1}
						trigger={trigger}
						onNameChange={setWorkflowName}
						onTriggerChange={setTrigger}
						onYamlToggle={() => setYamlOpen(!yamlOpen)}
						onDeploy={handleDeploy}
						yamlOpen={yamlOpen}
						deploying={deploying}
						onRun={workflowId ? handleRun : undefined}
					/>
				)}
				<ReactFlow
					nodes={displayNodes}
					edges={canvas.edges}
					onNodesChange={monitor.isMonitoring ? undefined : canvas.onNodesChange}
					onEdgesChange={monitor.isMonitoring ? undefined : canvas.onEdgesChange}
					onConnect={monitor.isMonitoring ? undefined : canvas.onConnect}
					onInit={setReactFlowInstance}
					onDrop={onDrop}
					onDragOver={onDragOver}
					onNodeClick={(_, node) => canvas.setSelectedNodeId(node.id)}
					onPaneClick={() => canvas.setSelectedNodeId(null)}
					nodeTypes={nodeTypes}
					edgeTypes={edgeTypes}
					nodesDraggable={!monitor.isMonitoring}
					nodesConnectable={!monitor.isMonitoring}
					fitView
					className="bg-canvas"
				>
					<Background color="var(--forge-color-border)" gap={20} size={1} />
					<Controls
						className="!bg-surface !border-border !shadow-sm !rounded-xl"
						showInteractive={false}
					/>
				</ReactFlow>
				{!monitor.isMonitoring && (
					<YamlPreview yaml={yaml} open={yamlOpen} onClose={() => setYamlOpen(false)} />
				)}
			</div>

			{/* Right: Step Inspector (monitor mode) or Config Panel (edit mode) */}
			{monitor.isMonitoring && selectedNode && selectedStepExecState ? (
				<div className="w-[300px] min-w-[300px] border-l border-border bg-surface">
					<StepInspectPanel
						step={selectedStepExecState}
						stepType={selectedNodeData?.stepType ?? ""}
						onApprove={monitor.approve}
					/>
				</div>
			) : (
				!monitor.isMonitoring &&
				selectedNode && (
					<div className="w-[300px] min-w-[300px] border-l border-border bg-surface">
						<StepConfigPanel
							data={selectedNode.data as unknown as StepNodeData}
							onChange={(data) => canvas.updateNodeData(selectedNode.id, data)}
							onDelete={() => canvas.removeNode(selectedNode.id)}
						/>
					</div>
				)
			)}
		</div>
	);
}
